import { useEffect, useState } from "react";
import { BarChart3, Users, Eye, BookOpen, Share2, MessageCircleHeart, SmilePlus, Camera, UserPlus, PenLine, Heart, Mail, Loader2, MousePointerClick, Wand2, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { DrillDialog } from "./DrillDialog";

const Card = ({ icon: Icon, label, value, sub, testId, onClick }) => (
  <button type="button" onClick={onClick} disabled={!onClick} title={onClick ? "Klik voor details" : undefined}
    className={`bg-card rounded-2xl p-5 flex items-center gap-4 text-left w-full ${onClick ? "cursor-pointer hover:ring-2 hover:ring-primary/30 active:scale-[0.99] transition-[box-shadow,transform] duration-200" : "cursor-default"}`}
    data-testid={testId}>
    <span className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
      <Icon className="w-5 h-5 text-primary" />
    </span>
    <div>
      <p className="font-mono font-extrabold text-2xl leading-none">{value}</p>
      <p className="text-xs text-muted-foreground mt-1">{label}{sub ? ` · ${sub}` : ""}</p>
    </div>
  </button>
);

const PATH_LABELS = { "/": "Landingspagina", "/journal/:id": "Reisdagboek (gasten)", "/share/:id": "Gedeelde trip", "/recap/:id": "Trip-recap", "/reply/:id": "Reactie-pagina", "/auth": "Inloggen/registreren", "/hoe-werkt-het": "Hoe werkt het", "/wat-is-nieuw": "Wat is nieuw", "/dashboard": "Dashboard", "/trip/:id": "Trip-detail", "/nieuwe-trip": "Wizard", "/admin": "Admin" };

const Funnel = ({ f }) => {
  const stages = [
    { key: "landing", label: "Landing-bezoekers", value: f.landing },
    { key: "showcase", label: "Voorbeeld-trip geklikt", value: f.showcase },
    { key: "registrations", label: "Registraties", value: f.registrations },
    { key: "trips", label: "Nieuwe trips", value: f.trips },
  ];
  const max = Math.max(1, ...stages.map((s) => s.value));
  return (
    <div className="bg-card rounded-2xl p-5 mb-5" data-testid="analytics-funnel">
      <p className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground mb-4">Conversie-funnel — laatste 30 dagen</p>
      <div className="space-y-2.5">
        {stages.map((s, i) => {
          const base = stages[0].value;
          const pct = i > 0 && base > 0 ? Math.round((s.value / base) * 100) : null;
          return (
            <div key={s.key} data-testid={`funnel-stage-${s.key}`} className="flex items-center gap-3">
              <span className="w-44 shrink-0 text-sm">{s.label}</span>
              <div className="flex-1 h-7 rounded-lg bg-secondary/60 overflow-hidden">
                <div className="h-full rounded-lg bg-primary/80 flex items-center justify-end px-2 min-w-[2rem] transition-[width] duration-500"
                  style={{ width: `${Math.max(6, (s.value / max) * 100)}%` }}>
                  <span className="font-mono font-bold text-xs text-primary-foreground">{s.value}</span>
                </div>
              </div>
              <span className="w-28 shrink-0 text-xs text-muted-foreground font-mono text-right">{pct != null ? `${pct}% v. bezoekers` : ""}</span>
            </div>
          );
        })}
      </div>
      <p className="text-[11px] text-muted-foreground mt-3">Percentages t.o.v. het aantal landing-bezoekers. Registraties en trips zijn 30-dagen-totalen (niet per sessie gekoppeld).</p>
    </div>
  );
};

const BaselineSetting = () => {
  const [value, setValue] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/settings").then((r) => setValue(r.data.stats_baseline ?? 2400)).catch(() => {}); }, []);
  const saveVal = async () => {
    setSaving(true);
    try {
      await api.patch("/admin/settings", { stats_baseline: Math.max(0, Number(value) || 0) });
      toast.success("Baseline opgeslagen — landing toont baseline + echte aantallen");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };
  if (value === null) return null;
  return (
    <div className="bg-card rounded-2xl p-5 mb-5 flex flex-wrap items-center gap-3" data-testid="analytics-baseline-setting">
      <div className="flex-1 min-w-[220px]">
        <p className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground">Landing-groeicijfers — baseline</p>
        <p className="text-xs text-muted-foreground mt-1">Wordt opgeteld bij de échte aantallen (trips gepland & EV-rijders) op de landingspagina. Zet op 0 voor alleen echte cijfers.</p>
      </div>
      <input data-testid="baseline-input" type="number" min="0" value={value} onChange={(e) => setValue(e.target.value)}
        className="w-28 rounded-xl border border-border bg-background px-3 py-2 font-mono text-sm outline-none focus:border-primary" />
      <button data-testid="baseline-save" onClick={saveVal} disabled={saving}
        className="rounded-full bg-primary hover:bg-[#1B4332] text-primary-foreground px-5 py-2 text-xs font-bold disabled:opacity-50 transition-[background-color] duration-200">
        {saving ? "Opslaan..." : "Opslaan"}
      </button>
    </div>
  );
};

export const AnalyticsAdmin = () => {
  const [data, setData] = useState(null);
  const [mailing, setMailing] = useState(false);
  const [drill, setDrill] = useState(null);
  const [showConversion, setShowConversion] = useState(false);
  useEffect(() => {
    api.get("/admin/analytics").then((r) => setData(r.data)).catch(() => {});
  }, []);
  const sendMail = async () => {
    setMailing(true);
    try {
      const r = await api.post("/admin/analytics/send-weekly");
      toast.success(`Weekstats gemaild naar ${r.data.sent_to.join(", ")}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setMailing(false);
    }
  };
  if (!data) return null;
  const maxViews = Math.max(1, ...data.per_day.map((d) => d.views));
  const e = data.engagement;
  return (
    <div data-testid="admin-analytics-section" className="mb-10">
      <div className="flex items-center justify-between gap-2 mb-4">
        <h2 className="font-heading font-bold text-lg flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-primary" /> Analytics
        </h2>
        <button data-testid="analytics-send-mail" onClick={sendMail} disabled={mailing}
          title="Elke maandagochtend automatisch — of stuur nu direct"
          className="rounded-full bg-secondary hover:bg-secondary/70 px-4 py-2 text-xs font-bold flex items-center gap-1.5 disabled:opacity-50 transition-[background-color] duration-200">
          {mailing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
          Mail weekstats
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        <Card icon={Users} label="Bezoekers (7 dagen)" value={data.visitors_7d} sub={`${data.visitors_30d} in 30 dagen`} testId="analytics-visitors" onClick={() => setDrill("traffic")} />
        <Card icon={Eye} label="Paginaweergaven (7 dagen)" value={data.views_7d} sub={`${data.views_30d} in 30 dagen`} testId="analytics-views" onClick={() => setDrill("traffic")} />
        <Card icon={BookOpen} label="Reisdagboek-views (30 d)" value={data.journal_views_30d} sub="gasten via magic link" testId="analytics-journal-views" onClick={() => setDrill("journal_views")} />
        <Card icon={Share2} label="Deelpagina-views (30 d)" value={data.share_views_30d} sub={`landing: ${data.landing_views_30d}`} testId="analytics-share-views" onClick={() => setDrill("share_views")} />
      </div>

      <div className="bg-card rounded-2xl p-5 mb-5" data-testid="analytics-chart">
        <p className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground mb-4">Weergaven per dag — laatste 14 dagen</p>
        <div className="flex items-end gap-1.5 h-28">
          {data.per_day.map((d) => (
            <div key={d.day} className="flex-1 flex flex-col items-center justify-end gap-1 h-full" title={`${d.day}: ${d.views} weergaven · ${d.visitors} bezoekers`}>
              <div className="w-full rounded-t-md bg-primary/70 hover:bg-primary transition-[background-color] duration-200"
                style={{ height: `${Math.max(3, (d.views / maxViews) * 100)}%` }} />
              <span className="text-[9px] font-mono text-muted-foreground">{d.day.slice(8)}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-5" data-testid="analytics-engagement">
        <Card icon={PenLine} label="Reislog-momenten" value={e.log_entries} testId="analytics-drill-log" onClick={() => setDrill("log_entries")} />
        <Card icon={Camera} label="Foto's & video's" value={e.photos} testId="analytics-drill-media" onClick={() => setDrill("media")} />
        <Card icon={MessageCircleHeart} label="Reacties" value={e.comments} testId="analytics-drill-comments" onClick={() => setDrill("comments")} />
        <Card icon={SmilePlus} label="Emoji-reacties" value={e.reactions} testId="analytics-drill-emoji" onClick={() => setDrill("emoji")} />
        <Card icon={Heart} label="Feedback-berichten" value={e.feedback} testId="analytics-drill-feedback" onClick={() => setDrill("feedback")} />
        <Card icon={UserPlus} label="Genodigden" value={e.guests} testId="analytics-drill-invites" onClick={() => setDrill("invites")} />
      </div>

      <DrillDialog metric={drill} onClose={() => setDrill(null)} />

      {data.top_pages.length > 0 && (
        <div className="bg-card rounded-2xl p-5" data-testid="analytics-top-pages">
          <p className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground mb-3">Populairste pagina's (30 dagen)</p>
          <div className="space-y-1.5">
            {data.top_pages.map((p) => (
              <div key={p.path} className="flex items-center justify-between text-sm">
                <span>{PATH_LABELS[p.path] || p.path} <span className="text-muted-foreground font-mono text-xs">{p.path}</span></span>
                <span className="font-mono font-bold">{p.views}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-5">
        <button data-testid="conversion-section-toggle" onClick={() => setShowConversion((v) => !v)}
          className="w-full bg-card rounded-2xl p-4 flex items-center justify-between text-left hover:ring-2 hover:ring-primary/20 transition-[box-shadow] duration-200">
          <span className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground flex items-center gap-2">
            <MousePointerClick className="w-4 h-4 text-primary" /> Conversie & instellingen (voorbeeld-trip, funnel, baseline)
          </span>
          <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${showConversion ? "rotate-180" : ""}`} />
        </button>
        {showConversion && (
          <div className="mt-4" data-testid="conversion-section">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
              <Card icon={MousePointerClick} label={`"Bekijk de voorbeeld-trip" (30 d)`} value={data.showcase_cta?.view_30d ?? 0} sub={`totaal: ${data.showcase_cta?.view_total ?? 0}`} testId="analytics-showcase-view" />
              <Card icon={Wand2} label={`"Plan deze trip zelf" (30 d)`} value={data.showcase_cta?.plan_30d ?? 0} sub={`totaal: ${data.showcase_cta?.plan_total ?? 0}`} testId="analytics-showcase-plan" />
            </div>
            {data.funnel && <Funnel f={data.funnel} />}
            <BaselineSetting />
          </div>
        )}
      </div>
    </div>
  );
};
